import { useState, useEffect } from "react";
import { Modal, Button, Spinner, Row, Col, Form } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL, HEADERS } from "../../../utils/constant";

const PermissionModal = ({
  show,
  handleClose,
  setAllowedPermissions,
  agentData,
}) => {
  const [permissions, setPermissions] = useState({});
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPermissions = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${BASE_URL}/api/all_permissions/`, {
          headers: HEADERS,
        });
        const fetchedPermissions = response.data;

        const selectedIds = agentData?.user_permissions
          ?.map((key) => {
            const matchingPermission = Object.values(fetchedPermissions)
              .flat()
              .find((perm) => perm.key === key);
            return matchingPermission?.id;
          })
          .filter(Boolean);

        setPermissions(fetchedPermissions);
        setSelectedPermissions(selectedIds || []);
      } catch (error) {
        toast.error("Failed to fetch permissions. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (show) {
      fetchPermissions();
    }
  }, [show, agentData]);

  const handleToggle = (permissionId) => {
    setSelectedPermissions((prev) =>
      prev?.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId],
    );
  };

  const handleSelectAll = (category, perms) => {
    const permIds = perms.map((perm) => perm.id);
    const allSelected = permIds.every((id) =>
      selectedPermissions?.includes(id),
    );

    if (allSelected) {
      setSelectedPermissions((prev) =>
        prev.filter((id) => !permIds?.includes(id)),
      );
    } else {
      setSelectedPermissions((prev) => [...new Set([...prev, ...permIds])]);
    }
  };

  const handleSave = () => {
    setAllowedPermissions(selectedPermissions);
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header
        closeButton
        style={{
          background: "primary",
          color: "#fff",
          borderBottom: "none",
        }}
      >
        <Modal.Title className="w-100 text-center">
          <i className="bi bi-sliders me-2"></i> Manage Access Permissions
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: "500px", overflowY: "auto" }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="success" />
          </div>
        ) : (
          Object.entries(permissions).map(([category, perms]) => {
            const allSelected = perms.every((perm) =>
              selectedPermissions.includes(perm.id),
            );

            return (
              <div
                key={category}
                className="mb-4"
                style={{
                  padding: "10px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                }}
              >
                <div
                  className="d-flex justify-content-between align-items-center mb-3"
                  style={{
                    backgroundColor: "#e8f5e9",
                    color: "#388e3c",
                    padding: "8px 12px",
                    borderRadius: "5px",
                  }}
                >
                  <h5 className="fw-bold m-0 d-flex align-items-center">
                    <i className="bi bi-folder2-open me-2"></i>
                    {category}
                  </h5>
                  <Form.Check
                    type="checkbox"
                    label="Select All"
                    checked={allSelected}
                    onChange={() => handleSelectAll(category, perms)}
                  />
                </div>

                <Row>
                  {perms.map((perm) => (
                    <Col md={6} sm={12} xs={12} key={perm.id} className="mb-3">
                      <div
                        className="d-flex justify-content-between align-items-center p-3 rounded shadow-sm"
                        style={{
                          backgroundColor: "#fff",
                          border: "1px solid #ddd",
                          transition: "all 0.3s ease-in-out",
                          color: "black",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.boxShadow =
                            "0 4px 10px rgba(0,0,0,0.1)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.boxShadow = "none")
                        }
                      >
                        <span className="d-flex align-items-center text-primary">
                          <i className="bi bi-check-circle me-2 text-success"></i>
                          {perm.name}
                        </span>
                        <Form.Check
                          type="switch"
                          className="form-switch custom-switch"
                          checked={selectedPermissions.includes(perm.id)}
                          onChange={() => handleToggle(perm.id)}
                          id={`perm-switch-${perm.id}`}
                          label=""
                        />
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            );
          })
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          style={{
            backgroundColor: "#9e9e9e",
            border: "none",
          }}
        >
          <i className="bi bi-x-circle me-1"></i> Close
        </Button>
        <Button
          variant="success"
          onClick={handleSave}
          style={{
            backgroundColor: "#4caf50",
            border: "none",
          }}
        >
          <i className="bi bi-check-circle me-1"></i> Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PermissionModal;
